"use client";

/**
 * Single source of truth for access/refresh token storage and renewal.
 *
 * Both refresh paths route through here: the auth provider's proactive
 * pre-expiry timer and the reactive 401 retry in use-api. Auth0 refresh
 * tokens are single-use, so two concurrent refreshes would invalidate each
 * other — the module-level `refreshInFlight` promise makes every caller in
 * the tab share one request.
 */

const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL || "http://localhost:8787";

const ACCESS_TOKEN_KEY = "access_token";
const REFRESH_TOKEN_KEY = "refresh_token";

let refreshInFlight: Promise<boolean> | null = null;

export function getAccessToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function storeTokens(accessToken: string, refreshToken?: string): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

export function clearTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

/**
 * Exchange the stored refresh token for a new access token. Concurrent
 * callers share the in-flight request and receive the same result.
 */
export function refreshAccessToken(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (!storedRefreshToken) return false;

      try {
        const res = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token: storedRefreshToken }),
        });
        const data = await res.json();

        if (data.data?.access_token) {
          storeTokens(data.data.access_token, data.data.refresh_token);
          return true;
        }
        // The refresh token was rejected — drop it so callers stop retrying.
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        return false;
      } catch {
        return false;
      }
    })().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/** Decode the JWT `exp` claim; returns expiry as epoch millis, or null. */
export function getTokenExpiry(token: string): number | null {
  try {
    const payload = JSON.parse(
      atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/** Clear stored tokens and force a clean re-authentication. */
export function redirectToLogin(): void {
  clearTokens();
  if (typeof window !== "undefined" && window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
}
